import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, FileText } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANONICAL = "https://alldaypowerwashing.com/blog";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles`);
        setArticles(data);
      } catch (e) {
        setError("Couldn't load articles right now.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B1726] text-white" data-testid="blog-page">
      <Helmet>
        <title>Blog — All Day Power Washing</title>
        <meta
          name="description"
          content="Tips, guides, and case studies on pressure washing, epoxy coatings, and property maintenance from All Day Power Washing in Colorado Springs."
        />
        <link rel="canonical" href={CANONICAL} />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-14 sm:pt-32 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1726] via-[#0F2540] to-[#0B1726]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, #1E88E5 0 2px, transparent 2px 60px)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Link
            to="/"
            data-testid="blog-back-home"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-heading uppercase tracking-wider mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-3 mb-4">
            <FileText className="w-4 h-4 text-[#E6B445]" />
            <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445]">
              Field Notes
            </span>
          </div>
          <h1 className="font-heading text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.95]">
            The <span className="text-[#1E88E5]">Blog.</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-slate-300 text-base sm:text-lg">
            Guides, tips, and case studies from real jobs — pressure washing,
            epoxy coatings, and property maintenance.
          </p>
        </div>
      </section>

      {/* Articles grid */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pb-20 sm:pb-28">
        {loading && (
          <div className="text-center py-16 text-slate-400" data-testid="blog-loading">
            Loading articles…
          </div>
        )}
        {!loading && error && (
          <div className="text-center py-16 text-slate-400">{error}</div>
        )}
        {!loading && !error && articles.length === 0 && (
          <div className="text-center py-20" data-testid="blog-empty">
            <div className="max-w-md mx-auto">
              <div className="font-heading text-lg font-bold uppercase tracking-widest text-slate-500 mb-3">
                No articles yet
              </div>
              <p className="text-slate-400 text-sm">
                We're publishing new guides regularly. Check back soon — or grab
                a free quote in the meantime.
              </p>
              <Link
                to="/quote"
                className="inline-flex items-center gap-2 mt-6 bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading uppercase tracking-wider text-sm px-6 py-3 rounded-sm transition-colors"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.05, 0.3) }}
                data-testid={`blog-card-${a.slug}`}
              >
                <Link
                  to={`/blog/${a.slug}`}
                  className="group block bg-[#142238] border border-slate-800 hover:border-[#1E88E5] rounded-md overflow-hidden transition-colors h-full flex flex-col"
                >
                  {a.hero_image_url ? (
                    <div className="aspect-video overflow-hidden bg-[#0B1726]">
                      <img
                        src={a.hero_image_url}
                        alt={a.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-[#0B1726] to-[#142238] flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-700" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 font-heading tracking-widest uppercase mb-3">
                      <Calendar className="w-3 h-3" />
                      <span>{fmtDate(a.published_at)}</span>
                    </div>
                    <h2 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-tight text-white leading-tight group-hover:text-[#E6B445] transition-colors">
                      {a.title}
                    </h2>
                    {a.meta_description && (
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">
                        {a.meta_description}
                      </p>
                    )}
                    <div className="mt-5 pt-4 border-t border-slate-800 flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-[#1E88E5]">
                      Read article
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
